import {
  type RefCallback,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { Booking } from "@/logic/api";
import type { BookingKey, LiveBookingChanges } from "./getLiveBookingChanges";

// timers are only a fallback for interrupted animations, so a slow first paint cannot cut motion short
const LIVE_ENTRY_FALLBACK_MS = 600;
const LIVE_UPDATE_FALLBACK_MS = 1200;
const LIVE_EXIT_FALLBACK_MS = 600;
const MAX_VISIBLE_EXITS = 4;
const MAX_INITIAL_ENTRY_DELAY_MS = 100;
const LIVE_LAYOUT_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";
const getLayoutDuration = (distance: number): number =>
  Math.min(280, 180 + Math.round(distance * 0.35));

export type LiveBookingMotion =
  | { kind: "enter"; revision: number }
  | { kind: "update"; revision: number; phase: "rise" | "rearm" | "fade" };

interface BookingLayoutPosition {
  top: number;
  left: number;
  relativeTop: number;
  relativeLeft: number;
  width: number;
  height: number;
}

interface ExitingBooking {
  key: string;
  booking: Booking;
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface InitialBookingEntry {
  delay: number;
  distance: string;
}

interface BookingViewMotionOptions {
  bookings: ReadonlyArray<Booking>;
  animationsEnabled: boolean;
  liveUpdatesEnabled: boolean;
  liveChanges: LiveBookingChanges | null;
  animateInitialEntry: boolean;
  onInitialEntryStarted?(): void;
}

const getScrollContainer = (
  element: HTMLElement | null,
): HTMLElement | null => {
  for (
    let parent = element?.parentElement;
    parent != null;
    parent = parent.parentElement
  ) {
    if (/(auto|scroll)/.test(window.getComputedStyle(parent).overflowY)) {
      return parent;
    }
  }
  return null;
};

const getViewportBounds = (scrollContainer: HTMLElement | null) =>
  scrollContainer?.getBoundingClientRect() ?? {
    top: 0,
    left: 0,
    bottom: window.innerHeight || document.documentElement.clientHeight,
    right: window.innerWidth || document.documentElement.clientWidth,
  };

const isInViewport = (
  bounds: { top: number; bottom: number; left: number; right: number },
  viewport: { top: number; bottom: number; left: number; right: number },
) =>
  bounds.bottom > viewport.top &&
  bounds.top < viewport.bottom &&
  bounds.right > viewport.left &&
  bounds.left < viewport.right;

const isPositionVisible = (
  position: BookingLayoutPosition,
  viewport: { top: number; bottom: number; left: number; right: number },
  scrollX: number,
  scrollY: number,
) => {
  const top = position.top - scrollY;
  const left = position.left - scrollX;
  return isInViewport(
    {
      top,
      bottom: top + position.height,
      left,
      right: left + position.width,
    },
    viewport,
  );
};

const ZERO_OFFSET = { x: 0, y: 0 };

const getTransformOffset = (element: HTMLElement) => {
  const transform = window.getComputedStyle(element).transform;
  if (!transform || transform === "none") return ZERO_OFFSET;
  const matrix = new DOMMatrixReadOnly(transform);
  return { x: matrix.m41, y: matrix.m42 };
};

const forEachVisibleBooking = (
  bookingIds: ReadonlyArray<BookingKey>,
  bookingElements: ReadonlyMap<BookingKey, HTMLDivElement>,
  scrollContainer: HTMLElement | null,
  visit: (
    id: BookingKey,
    element: HTMLDivElement,
    bounds: DOMRect,
    offset: { x: number; y: number },
    isVisible: boolean,
  ) => void,
  activeAnimations?: ReadonlyMap<BookingKey, Animation>,
  includeAdjacent = false,
): void => {
  const viewport = getViewportBounds(scrollContainer);
  const visited = new Set<BookingKey>();
  const offsets = new Map<BookingKey, { x: number; y: number }>();
  const getOffset = (id: BookingKey, element: HTMLElement) => {
    if (!activeAnimations?.has(id)) return ZERO_OFFSET;
    let offset = offsets.get(id);
    if (offset == null) {
      offset = getTransformOffset(element);
      offsets.set(id, offset);
    }
    return offset;
  };
  const getInFlowBounds = (
    id: BookingKey,
    bounds: DOMRect,
    element: HTMLElement,
  ) => {
    if (!activeAnimations?.has(id)) return bounds;
    const { y } = getOffset(id, element);
    return { top: bounds.top - y, bottom: bounds.bottom - y };
  };
  const visitMeasured = (
    id: BookingKey,
    element: HTMLDivElement,
    bounds: DOMRect,
    isVisible: boolean,
  ) => {
    if (isVisible) visited.add(id);
    visit(id, element, bounds, getOffset(id, element), isVisible);
  };

  // Cards are in document order. Find the first visible row without measuring
  // every card above the viewport. Use in-flow bounds during active moves so
  // transformed rows cannot break the binary search's ordering.
  let low = 0;
  let high = bookingIds.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    const id = bookingIds[middle];
    const element = bookingElements.get(id);
    if (element == null) {
      // Incomplete refs should drop this one motion pass, never trigger a
      // synchronous scan of every row above the viewport.
      return;
    }
    const bounds = element.getBoundingClientRect();
    if (getInFlowBounds(id, bounds, element).bottom <= viewport.top) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  if (includeAdjacent && low > 0) {
    const id = bookingIds[low - 1];
    const element = bookingElements.get(id);
    if (element != null) {
      const bounds = element.getBoundingClientRect();
      visitMeasured(id, element, bounds, isInViewport(bounds, viewport));
    }
  }

  for (let index = low; index < bookingIds.length; index++) {
    const id = bookingIds[index];
    const element = bookingElements.get(id);
    if (element == null) continue;
    const bounds = element.getBoundingClientRect();
    if (getInFlowBounds(id, bounds, element).top >= viewport.bottom) {
      if (includeAdjacent) {
        visitMeasured(id, element, bounds, isInViewport(bounds, viewport));
      }
      break;
    }
    if (isInViewport(bounds, viewport)) {
      visitMeasured(id, element, bounds, true);
    }
  }

  // A moving card can be visually inside the viewport while its in-flow slot
  // is outside it. Check only those active cards instead of scanning from row 0.
  activeAnimations?.forEach((_, id) => {
    if (visited.has(id)) return;
    const element = bookingElements.get(id);
    if (element == null) return;
    const bounds = element.getBoundingClientRect();
    if (isInViewport(bounds, viewport)) {
      visitMeasured(id, element, bounds, true);
    }
  });
};

const getInitialEntryDelay = (visibleIndex: number): number =>
  Math.round(MAX_INITIAL_ENTRY_DELAY_MS * (visibleIndex / (visibleIndex + 1)));

const getInitialEntryDistance = (visibleIndex: number): string => {
  if (visibleIndex < 2) return "8px";
  if (visibleIndex < 4) return "4px";
  return "0px";
};

export const useBookingViewMotion = ({
  bookings,
  animationsEnabled,
  liveUpdatesEnabled,
  liveChanges,
  animateInitialEntry,
  onInitialEntryStarted,
}: BookingViewMotionOptions) => {
  const liveMotionEnabled = animationsEnabled && liveUpdatesEnabled;
  const listElement = useRef<HTMLDivElement>(null);
  const bookingElements = useRef(new Map<BookingKey, HTMLDivElement>());
  const bookingRefCallbacks = useRef(
    new Map<BookingKey, RefCallback<HTMLDivElement>>(),
  );
  const getBookingRef = useCallback(
    (id: BookingKey): RefCallback<HTMLDivElement> => {
      let callback = bookingRefCallbacks.current.get(id);
      if (callback == null) {
        callback = (element) => {
          if (element == null) {
            bookingElements.current.delete(id);
            bookingRefCallbacks.current.delete(id);
          } else {
            bookingElements.current.set(id, element);
          }
        };
        bookingRefCallbacks.current.set(id, callback);
      }
      return callback;
    },
    [],
  );

  const [initialBookingIds] = useState(() =>
    animateInitialEntry ? bookings.map((booking) => booking.uuid) : [],
  );
  const [initialEntryByBookingId, setInitialEntryByBookingId] =
    useState<ReadonlyMap<BookingKey, InitialBookingEntry> | null>(() =>
      animateInitialEntry ? null : new Map(),
    );
  const shouldTrackBookingElements =
    liveMotionEnabled ||
    (animateInitialEntry && initialEntryByBookingId == null);

  useLayoutEffect(() => {
    if (!animateInitialEntry || initialEntryByBookingId != null) return;

    const visibleBookingIds: BookingKey[] = [];
    forEachVisibleBooking(
      initialBookingIds,
      bookingElements.current,
      getScrollContainer(listElement.current),
      (id) => visibleBookingIds.push(id),
    );
    // The measured entry styles must be applied before the first paint.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInitialEntryByBookingId(
      new Map(
        visibleBookingIds.map((id, index) => [
          id,
          {
            delay: getInitialEntryDelay(index),
            distance: getInitialEntryDistance(index),
          },
        ]),
      ),
    );
    onInitialEntryStarted?.();
  }, [
    animateInitialEntry,
    initialBookingIds,
    initialEntryByBookingId,
    onInitialEntryStarted,
  ]);

  const consumedLiveRevision = useRef(0);
  const liveMotionRevision = useRef(0);
  const liveMotionTimers = useRef(
    new Map<BookingKey, { handle: number; revision: number }>(),
  );
  const exitTimers = useRef(new Map<string, number>());
  const layoutPositions = useRef(new Map<BookingKey, BookingLayoutPosition>());
  const previousBookingIds = useRef<ReadonlyArray<BookingKey>>([]);
  const previousBookingsById = useRef<ReadonlyMap<BookingKey, Booking>>(new Map());
  const visibleBookingIds = useRef<ReadonlySet<BookingKey>>(new Set());
  const previousScrollPosition = useRef({ x: 0, y: 0 });
  const measuredBookings = useRef<ReadonlyArray<Booking> | null>(null);
  const layoutAnimations = useRef(new Map<BookingKey, Animation>());
  const [liveMotionByBookingId, setLiveMotionByBookingId] = useState<
    ReadonlyMap<BookingKey, LiveBookingMotion>
  >(new Map());
  const [exitingBookings, setExitingBookings] = useState<
    ReadonlyArray<ExitingBooking>
  >([]);
  const finishLiveEntry = useCallback((id: BookingKey, revision: number): void => {
    const timer = liveMotionTimers.current.get(id);
    if (timer?.revision === revision) {
      window.clearTimeout(timer.handle);
      liveMotionTimers.current.delete(id);
    }
    setLiveMotionByBookingId((current) => {
      if (current.get(id)?.revision !== revision) return current;
      const next = new Map(current);
      next.delete(id);
      return next;
    });
  }, []);
  const finishLiveUpdatePhase = useCallback(
    (
      id: BookingKey,
      revision: number,
      phase: "rise" | "rearm" | "fade",
    ): void => {
      if (phase === "fade") {
        const timer = liveMotionTimers.current.get(id);
        if (timer?.revision === revision) {
          window.clearTimeout(timer.handle);
          liveMotionTimers.current.delete(id);
        }
      }
      setLiveMotionByBookingId((current) => {
        const motion = current.get(id);
        if (
          motion?.kind !== "update" ||
          motion.revision !== revision ||
          motion.phase !== phase
        ) {
          return current;
        }
        const next = new Map(current);
        if (phase === "rearm") {
          next.set(id, { ...motion, phase: "rise" });
        } else if (phase === "rise") {
          next.set(id, { ...motion, phase: "fade" });
        } else {
          next.delete(id);
        }
        return next;
      });
    },
    [],
  );
  const finishLiveExit = useCallback((key: string): void => {
    const timer = exitTimers.current.get(key);
    if (timer != null) {
      window.clearTimeout(timer);
      exitTimers.current.delete(key);
    }
    setExitingBookings((current) =>
      current.some((item) => item.key === key)
        ? current.filter((item) => item.key !== key)
        : current,
    );
  }, []);

  useLayoutEffect(() => {
    if (!liveMotionEnabled) {
      layoutAnimations.current.forEach((animation) => animation.cancel());
      layoutAnimations.current.clear();
      exitTimers.current.forEach((timer) => window.clearTimeout(timer));
      exitTimers.current.clear();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setExitingBookings((current) => (current.length > 0 ? [] : current));
      layoutPositions.current.clear();
      previousBookingIds.current = [];
      previousBookingsById.current = new Map();
      visibleBookingIds.current = new Set();
      measuredBookings.current = null;
      return;
    }
    if (measuredBookings.current === bookings) return;

    const nextIds = bookings.map((booking) => booking.uuid);
    const previousIds = previousBookingIds.current;
    const hasIdSequenceChanged =
      measuredBookings.current != null &&
      (previousIds.length !== nextIds.length ||
        nextIds.some((id, index) => id !== previousIds[index]));
    const previousBookings = previousBookingsById.current;
    const isLiveStructuralChange =
      liveChanges != null &&
      liveChanges.revision !== consumedLiveRevision.current &&
      (liveChanges.orderChanged ||
        liveChanges.addedUuids.size > 0 ||
        liveChanges.removedUuids.size > 0);
    const scrollContainer = getScrollContainer(listElement.current);
    const scrollX = scrollContainer?.scrollLeft ?? window.scrollX;
    const scrollY = scrollContainer?.scrollTop ?? window.scrollY;
    const hasScrolled =
      Math.abs(scrollX - previousScrollPosition.current.x) > 1 ||
      Math.abs(scrollY - previousScrollPosition.current.y) > 1;
    const shouldAnimateLayout =
      isLiveStructuralChange &&
      !hasScrolled &&
      typeof Element.prototype.animate === "function" &&
      !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const listBounds = listElement.current?.getBoundingClientRect();
    const viewport = getViewportBounds(scrollContainer);
    const nextPositions = new Map<BookingKey, BookingLayoutPosition>();
    const nextVisibleIds = new Set<BookingKey>();
    const movements: Array<{
      id: BookingKey;
      element: HTMLDivElement;
      x: number;
      y: number;
    }> = [];

    // Read only rows in the scroll viewport, including any active card whose
    // transform currently brings it in from an off-screen in-flow position.
    forEachVisibleBooking(
      nextIds,
      bookingElements.current,
      scrollContainer,
      (id, element, rect, { x: offsetX, y: offsetY }, isVisible) => {
        if (isVisible) nextVisibleIds.add(id);
        const baseLeft = rect.left - offsetX;
        const baseTop = rect.top - offsetY;
        nextPositions.set(id, {
          left: baseLeft + scrollX,
          top: baseTop + scrollY,
          relativeTop: baseTop - (listBounds?.top ?? 0),
          relativeLeft: baseLeft - (listBounds?.left ?? 0),
          width: rect.width,
          height: rect.height,
        });

        const previous = layoutPositions.current.get(id);
        if (shouldAnimateLayout && previous != null) {
          const x = previous.left - scrollX + offsetX - baseLeft;
          const y = previous.top - scrollY + offsetY - baseTop;
          if (Math.abs(x) > 0.5 || Math.abs(y) > 0.5) {
            movements.push({ id, element, x, y });
          }
        }
      },
      layoutAnimations.current,
      true,
    );

    if (isLiveStructuralChange && listBounds != null && liveChanges != null) {
      const exits: ExitingBooking[] = [];
      const availableExitSlots = Math.max(
        0,
        MAX_VISIBLE_EXITS - exitTimers.current.size,
      );
      liveChanges.removedUuids.forEach((id) => {
        if (exits.length >= availableExitSlots) return;
        // A row already in flight has no stable final screen position to clone.
        if (layoutAnimations.current.has(id)) return;
        const position = layoutPositions.current.get(id);
        const booking = previousBookings.get(id);
        if (
          position == null ||
          booking == null ||
          !isPositionVisible(position, viewport, scrollX, scrollY)
        ) return;
        exits.push({
          key: `${liveChanges.revision}:${id}`,
          booking,
          top: position.relativeTop,
          left: position.relativeLeft,
          width: position.width,
          height: position.height,
        });
      });
      if (exits.length > 0) {
        // The outgoing cards are positioned before the next paint.
        setExitingBookings((current) => [...current, ...exits]);
        exits.forEach(({ key }) => {
          const timer = window.setTimeout(
            () => finishLiveExit(key),
            LIVE_EXIT_FALLBACK_MS,
          );
          exitTimers.current.set(key, timer);
        });
      }
    }

    if (hasIdSequenceChanged || isLiveStructuralChange) {
      layoutAnimations.current.forEach((animation) => animation.cancel());
      layoutAnimations.current.clear();
      // Movements are collected only for an unscrolled live layout change.
      for (const { id, element, x, y } of movements) {
        const animation = element.animate(
          [
            { transform: `translate3d(${x}px, ${y}px, 0)` },
            { transform: "translate3d(0, 0, 0)" },
          ],
          {
            duration: getLayoutDuration(Math.hypot(x, y)),
            easing: LIVE_LAYOUT_EASING,
          },
        );
        layoutAnimations.current.set(id, animation);
        animation.onfinish = () => {
          if (layoutAnimations.current.get(id) === animation) {
            layoutAnimations.current.delete(id);
          }
        };
      }
    }
    layoutPositions.current = nextPositions;
    previousBookingIds.current = nextIds;
    visibleBookingIds.current = nextVisibleIds;
    const nextBookingsById = new Map<BookingKey, Booking>();
    for (const booking of bookings) nextBookingsById.set(booking.uuid, booking);
    previousBookingsById.current = nextBookingsById;
    previousScrollPosition.current = { x: scrollX, y: scrollY };
    measuredBookings.current = bookings;
  }, [bookings, finishLiveExit, liveChanges, liveMotionEnabled]);

  useLayoutEffect(() => {
    if (!liveMotionEnabled) {
      liveMotionTimers.current.forEach(({ handle }) =>
        window.clearTimeout(handle),
      );
      liveMotionTimers.current.clear();
      if (liveChanges != null) {
        consumedLiveRevision.current = liveChanges.revision;
      }
      // Clear a visible cue before painting with motion disabled.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLiveMotionByBookingId((current) =>
        current.size > 0 ? new Map() : current,
      );
      return;
    }

    const isNewLiveResult =
      liveChanges != null &&
      liveChanges.revision !== consumedLiveRevision.current;
    if (!isNewLiveResult && liveMotionTimers.current.size === 0) return;
    if (isNewLiveResult && liveChanges != null) {
      consumedLiveRevision.current = liveChanges.revision;
    }

    const presentBookings = previousBookingsById.current;
    const changedMotions = new Map<BookingKey, LiveBookingMotion>();
    if (isNewLiveResult && liveChanges != null) {
      const markVisible = (id: BookingKey, kind: LiveBookingMotion["kind"]) => {
        if (!presentBookings.has(id) || !visibleBookingIds.current.has(id)) return;
        const revision = ++liveMotionRevision.current;
        changedMotions.set(
          id,
          kind === "enter"
            ? { kind, revision }
            : { kind, revision, phase: "rise" },
        );
      };
      liveChanges.addedUuids.forEach((id) => markVisible(id, "enter"));
      liveChanges.updatedUuids.forEach((id) => {
        if (!liveChanges.addedUuids.has(id)) markVisible(id, "update");
      });
    }

    for (const [id, timer] of liveMotionTimers.current) {
      if (!presentBookings.has(id)) {
        window.clearTimeout(timer.handle);
        liveMotionTimers.current.delete(id);
      }
    }

    setLiveMotionByBookingId((currentMotions) => {
      const nextMotions = new Map(currentMotions);
      let didChange = changedMotions.size > 0;
      for (const id of nextMotions.keys()) {
        if (!presentBookings.has(id)) {
          nextMotions.delete(id);
          didChange = true;
        }
      }
      changedMotions.forEach((motion, id) => {
        const previous = nextMotions.get(id);
        nextMotions.set(
          id,
          motion.kind === "update" && previous?.kind === "update"
            ? { ...motion, phase: "rearm" }
            : motion,
        );
      });
      return didChange ? nextMotions : currentMotions;
    });

    changedMotions.forEach((motion, id) => {
      const currentTimer = liveMotionTimers.current.get(id);
      if (currentTimer != null) window.clearTimeout(currentTimer.handle);
      const duration =
        motion.kind === "enter"
          ? LIVE_ENTRY_FALLBACK_MS
          : LIVE_UPDATE_FALLBACK_MS;
      const timer = window.setTimeout(() => {
        if (liveMotionTimers.current.get(id)?.revision === motion.revision) {
          liveMotionTimers.current.delete(id);
        }
        setLiveMotionByBookingId((currentMotions) => {
          if (currentMotions.get(id)?.revision !== motion.revision) {
            return currentMotions;
          }
          const nextMotions = new Map(currentMotions);
          nextMotions.delete(id);
          return nextMotions;
        });
      }, duration);
      liveMotionTimers.current.set(id, {
        handle: timer,
        revision: motion.revision,
      });
    });
  }, [bookings, liveChanges, liveMotionEnabled]);

  useEffect(
    () => () => {
      liveMotionTimers.current.forEach(({ handle }) =>
        window.clearTimeout(handle),
      );
      liveMotionTimers.current.clear();
      exitTimers.current.forEach((timer) => window.clearTimeout(timer));
      exitTimers.current.clear();
      layoutAnimations.current.forEach((animation) => animation.cancel());
      layoutAnimations.current.clear();
    },
    [],
  );

  return {
    listElement,
    getBookingRef,
    shouldTrackBookingElements,
    initialEntryByBookingId,
    liveMotionByBookingId,
    exitingBookings,
    finishLiveEntry,
    finishLiveUpdatePhase,
    finishLiveExit,
  };
};
